/* eslint-disable react-hooks/exhaustive-deps */
// vendors
import { useQuery, queryCache } from 'react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';

// lego
import { Tabs } from 'lego-dist';

// components
import Breadcrumbs from 'components/shared/Breadcrumbs';
import ClassroomDetailsInfo from './ClassroomDetailsInfo';
import ClassroomDetailsGeneral from './ClassroomDetailsGeneral';
import ClassroomDetailsTeachers from './ClassroomDetailsTeachers';
import ClassroomDetailsHeader from './ClassroomDetailsHeader';

// constants
import { classroomQuery } from '../queryConfig';
import { ClassroomDetailsActions } from './ClassroomDetailsActions';
import { APP_SECTIONS_PATHS } from 'constants/routes';

// hooks
import { useRouter } from 'routes';

// styles
import { Body, Wrapper, Container } from './styles';
import useTeacher from 'hooks/useTeacher';
import useCurrentUser from 'hooks/useCurrentUser';
import { ActionPermission } from 'lib/specialPermissions';
import GroupService from 'services/GroupService';

interface IClassroomDetailsProps {}

const { getClassroomQuery } = classroomQuery;

const tabs = [
  {
    freemium: true,
    type: 'General',
    label: <Trans>settings:CENTER_SECTION.STUDENTS</Trans>,
    View: ClassroomDetailsGeneral,
  },
  {
    freemium: true,
    disabled: false,
    type: 'Teachers',
    label: <Trans>settings:CENTER_SECTION.TABS.TEACHERS</Trans>,
    View: ClassroomDetailsTeachers,
  },
];

const groupService = GroupService.getInstance();
let CurrentTab = tabs[0];
// eslint-disable-next-line import/prefer-default-export
export const ClassroomDetails: React.FC<IClassroomDetailsProps> = () => {
  const { t: translate } = useTranslation();

  const {
    currentParamsRef: {
      current: { centerId, groupId, externalId, externalIdValue, tab: sectionSelected },
    },
  } = useRouter();

  if (sectionSelected) {
    CurrentTab = tabs[1];
  }

  const {
    isError,
    data = { group: {} },
    refetch: fetchClassroom,
    isSuccess: fechedClassroom,
  }: any = useQuery(
    getClassroomQuery.key,
    () => getClassroomQuery.resource(externalId || externalIdValue),
    getClassroomQuery.config,
  );

  const [selectedItems, setSelectedItems] = useState<any>({});
  const [searchedData, setSearchedData] = useState<any>([]);
  const [currentTabData, setCurrentTabData] = useState<any>([]);
  const [teacherList, setTeacherList] = useState<any>();
  const { getTeacherList } = useTeacher();
  const { can } = useCurrentUser();

  const currentDataShow = useMemo(() => {
    if (searchedData.length > 0) {
      return searchedData;
    }
    return currentTabData;
  }, [searchedData, currentTabData]);

  const showSearcher = useMemo(() => {
    const isGeneralTab = CurrentTab.type === 'General';
    const selectedItemsLength = Object.keys(selectedItems).length;

    return !isGeneralTab || (isGeneralTab && selectedItemsLength === 0);
  }, [CurrentTab, selectedItems]);

  useEffect(() => {
    // TODO: Take a look in case of errors.
    getTeacherList(centerId, groupId)
      .then((res: any) => {
        setTeacherList(res?.filter((item: any) => item.type === 'Teacher'));
      })
      .catch((error: any) => console.log('error', error));
  }, []);

  /* Takes data from the classrooms' API response to pass it to each component as appropriate */
  const getDataByTab = (type: any) => {
    const tabsData: any = {
      General: data?.group?.babies || [],
      Teachers: teacherList || [],
      Configuration: [],
    };
    return tabsData[type];
  };

  const refreshTeacherList = () =>
    getTeacherList(centerId, groupId)
      .then((res: any) => {
        setTeacherList(res?.filter((item: any) => item.type === 'Teacher'));
      })
      .catch((error) => console.log('error', error));

  const links = useMemo(() => {
    const centerName = localStorage.ACTIVE_CENTER_NAME;
    return [
      {
        name: translate('settings:SETTINGS'),
        url: APP_SECTIONS_PATHS.SETTINGS,
      },
      {
        name: centerName || translate('settings:CENTER_SECTION.CENTER'),
        url: `${APP_SECTIONS_PATHS.CENTER_SETTINGS}`,
        params: {
          centerId,
          centerName,
        },
      },
    ];
  }, [centerId]);

  useMemo(() => {
    CurrentTab = tabs[0];
    fetchClassroom().then(({ group }: any) => groupService.setActiveGroup(group));
  }, [centerId, groupId]);

  const setTabData = () => {
    const tabData = getDataByTab(CurrentTab?.type);
    setCurrentTabData(tabData);
  };

  const onQueryChange = (_: any, { queryKey }: any) => {
    if (queryKey[0] === getClassroomQuery.key) {
      setTabData();
    }
  };

  useEffect(() => {
    const currentGroup = data?.group || {};
    if (Object.keys(currentGroup)?.length > 0) {
      setTabData();
      // To listen when classroom query changes
      queryCache.subscribe(onQueryChange);
    }
  }, [fechedClassroom]);

  const onSearchData = (data: any) => {
    setSearchedData([...data]);
  };

  const onClickTab = ({ currentSelected }: any) => {
    CurrentTab = currentSelected;
    setSearchedData([]);
    setCurrentTabData(getDataByTab(currentSelected?.type));
  };

  const primaryButtonProps = {
    text:
      CurrentTab.type === 'General'
        ? !showSearcher
          ? translate('MOVE_TO')
          : translate('settings:INVITE_STUDENT')
        : translate('settings:ADD_TEACHER'),
    tab: CurrentTab.type,
  };

  const getCountText = () => {
    const count = `( ${currentDataShow?.length || 0} )`;
    return (
      <>
        {CurrentTab.label} {count}
      </>
    );
  };

  useEffect(() => {
    refreshTeacherList();
  }, []);

  useMemo(() => {
    const tabData = getDataByTab(CurrentTab?.type);
    setCurrentTabData(tabData);
  }, [teacherList]);

  useEffect(() => {
    setCurrentTabData(data?.group?.babies);
  }, [data]);

  const onSelectAllItems = (checked: boolean) => {
    if (checked) {
      const currentSelectedData: any = {};
      currentDataShow.forEach((dataItem: any) => {
        currentSelectedData[dataItem.id] = dataItem;
      });
      setSelectedItems(currentSelectedData);
    } else {
      setSelectedItems([]);
    }
  };

  const onSelectItem = ({ selectedItem }: any) => {
    const currentSelectedItems = { ...selectedItems };
    if (currentSelectedItems[selectedItem?.id]) {
      delete currentSelectedItems[selectedItem.id];
    } else {
      currentSelectedItems[selectedItem.id] = selectedItem;
    }
    setSelectedItems(currentSelectedItems);
  };

  const onMovedItems = () => {
    const updatedItems = currentTabData.filter(
      (tabData: any) => !selectedItems[tabData.id],
    );
    onSelectAllItems(false);
    setCurrentTabData(updatedItems);
  };

  const isSelectedAll = useMemo(() => (
    currentTabData?.length
    && Object.keys(selectedItems)?.length === currentTabData?.length
  ), [currentTabData, selectedItems]);

  return (
    <Wrapper>
      <Container>

        <Breadcrumbs links={links} color="#32AFE0" />
        {!isError ? (
          <>
            <ClassroomDetailsHeader
              canDeleteClassroom={can(ActionPermission.DELETE_CLASSROOMS)}
              groupId={groupId}
              centerId={centerId}
              externalId={externalId}
              classroomName={data?.group?.name}
              studentsLength={data?.group?.babies?.length}
            />

            <ClassroomDetailsInfo
              classroomData={data?.group || data?.updatedGroup}
            />
            <Body>
              <Tabs items={tabs} active={CurrentTab} onClickItem={onClickTab} />
              {CurrentTab?.type !== 'Configuration' && (
                <ClassroomDetailsActions
                  data={currentTabData}
                  onSearch={onSearchData}
                  classroom={data.group}
                  countText={getCountText()}
                  showSearcher={showSearcher}
                  onMovedItems={onMovedItems}
                  selectedItems={selectedItems}
                  onSelectAll={onSelectAllItems}
                  selectedAll={isSelectedAll}
                  primaryButtonProps={primaryButtonProps}
                  refreshTeacherList={refreshTeacherList}
                  withSelectAllCheckbox={
                    CurrentTab.type === 'General' &&
                    can(ActionPermission.MOVE_STUDENTS_FROM_CLASSROOM)
                  }
                />
              )}
              {Object.keys(data?.group || data?.updatedGroup).length > 0 && (
                <CurrentTab.View
                  data={currentDataShow}
                  onSelectItem={onSelectItem}
                  selectedItems={selectedItems}
                  handleOnFinishDeleteTeacherFlow={refreshTeacherList}
                  // min={data?.group?.max_age}
                  // max={data?.group?.min_age}
                  classroomName={data?.group?.name}
                />
              )}
            </Body>
          </>
        ) : (
          <>An error has occurred...</>
        )}
      </Container>
    </Wrapper>
  );
};
